export function buildRunSummary(results, { mode, model, appVersion, inspectionMode, duration, notes, thinkingLevel, temperatureMode, mediaResolution }) {
  // stability実行はr.match/r.run.analyzeMsではなくr.stability/r.runs（各回の結果配列）を持つ形になる
  const matchCount = mode === 'stability'
    ? results.filter(r => !r.error && r.stability?.consistent).length
    : results.filter(r => !r.error && r.match).length;
  const errorCount = results.filter(r => r.error).length;
  const analyzeTimes = (mode === 'stability'
    ? results.flatMap(r => (r.runs || []).filter(x => x && !x.error && x.analyzeMs).map(x => x.analyzeMs))
    : results.filter(r => r.run?.analyzeMs).map(r => r.run.analyzeMs)
  ).sort((a, b) => a - b);
  const median = analyzeTimes.length > 0
    ? analyzeTimes[Math.floor(analyzeTimes.length / 2)]
    : null;

  return {
    mode,
    status: 'completed',
    model,
    app_version: appVersion,
    inspection_mode: inspectionMode || 'auto',
    iterations: mode === 'stability' ? 10 : 1,
    label_count: results.length,
    match_count: matchCount,
    error_count: errorCount,
    duration_ms: duration ? Math.round(duration) : null,
    latency_median_ms: median,
    notes: notes || null,
    thinking_level: thinkingLevel || 'default',
    temperature_mode: temperatureMode || 'default',
    media_resolution: mediaResolution || 'default',
  };
}

export function buildRunItems(results) {
  return results.map(r => {
    // stability実行の行はr.stability（安定度サマリ）とr.runs（各回の結果）を持つ
    if (r.stability) {
      const times = (r.runs || []).filter(x => x && !x.error && x.analyzeMs).map(x => x.analyzeMs).sort((a, b) => a - b);
      const medianMs = times.length > 0 ? times[Math.floor(times.length / 2)] : null;
      const breakdown = Object.entries(r.stability.counts || {}).map(([status, n]) => `${status}:${n}回`).join(' / ');
      return {
        reference_label_id: r.ref?.id || null,
        label: r.ref?.label || '—',
        expected_status: null,
        actual_status: r.stability.majority || null,
        match: r.stability.consistent ?? null,
        reason: `安定度${r.stability.score}%（有効${r.validRuns ?? r.stability.total}/10回・内訳 ${breakdown}）`,
        analyze_ms: medianMs,
        error: r.error || null,
      };
    }
    return {
      reference_label_id: r.ref?.id || null,
      label: r.ref?.label || '—',
      expected_status: r.expected || null,
      actual_status: r.run?.match_status || r.actual || null,
      match: r.match ?? null,
      reason: r.run?.reason || null,
      analyze_ms: r.run?.analyzeMs || null,
      error: r.error || null,
      // match_statusはapi/_validateResponse.jsのルールでreason文とは無関係に上書きされることがあるため、
      // 上書きの根拠になった内訳も残す（誤答調査用）
      label_consistency: r.run?.label_consistency || null,
      quantity_status: r.run?.quantity_status || null,
      label_quantity: r.run?.label_quantity || null,
      label_quantity_number: r.run?.label_quantity_number ?? null,
      detected_quantity: r.run?.detected_quantity ?? null,
      // 撮影画像のピントの鋭さと「画像不鮮明」の判断（api/_imageQuality.js）
      image_quality: r.run?.image_quality ?? null,
    };
  });
}

export function diffRuns(itemsA, itemsB) {
  const mapA = new Map(itemsA.map(i => [i.reference_label_id || i.label, i]));
  const mapB = new Map(itemsB.map(i => [i.reference_label_id || i.label, i]));
  const changes = [];

  for (const [key, b] of mapB) {
    const a = mapA.get(key);
    if (!a) {
      changes.push({ label: b.label, type: 'added', before: null, after: b });
    } else if (a.match !== b.match || a.actual_status !== b.actual_status) {
      changes.push({ label: b.label, type: 'changed', before: a, after: b });
    }
  }
  for (const [key, a] of mapA) {
    if (!mapB.has(key)) {
      changes.push({ label: a.label, type: 'removed', before: a, after: null });
    }
  }
  return changes;
}
